import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, interval, Observable, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AppConfiguration } from '../classes/configuration';
import { Events } from '../classes/events';
import { Profile } from '../classes/profile';
import { EVENT_DELTAWORKER_TIMER } from '../common/constants';
import { ConfigurationService } from './configuration.service';
import { LoggerService } from './logger.service';
import { MsalConfigService } from './msal-config.service';
import { OutboundAircraftMarkersService } from './outbound-aircraft-markers.service';
import { ProcessDataService } from './process-data.service';
import { ProfileService } from './profile.service';

const useImMemoryWebApi = !environment.production && environment.inMemoryWebApiEnabled;
const worker = new Worker(new URL("../workers/event-delta.worker", import.meta.url));
export class CurrentTaskCounts{
  currentUnassignedTaskCount = 0;
  currentDriverTaskCount = 0;
}

@Injectable({
  providedIn: 'root'
})
export class EventsDeltaService implements OnDestroy {
  eventData$: BehaviorSubject<Events> = new BehaviorSubject(null);
  public lastUpdateTimestamp$: BehaviorSubject<any> = new BehaviorSubject(null);
  unsubscribe$: Subject<boolean> = new Subject();
  events: Events;
  eventsURL: string;
  configuration: AppConfiguration;
  profile: Profile;
  interval: Subscription = new Subscription();

  taskCounts = new CurrentTaskCounts();
  // currentUnassignedTaskCount = 0;
  // currentDriverTaskCount = 0;



  constructor(
    private logger: LoggerService,
    private configurationService: ConfigurationService,
    private profileService: ProfileService,
    private http: HttpClient,
    private processDataService: ProcessDataService,
    private outboundAircraftMarkersService: OutboundAircraftMarkersService,
    private msalConfigService: MsalConfigService
  ) {
    this.subscribeToConfiguration();
    this.subscribeToProfile();
   }

  subscribeToConfiguration(): void{
    this.configurationService.getConfiguration()
      .pipe(
        takeUntil(this.unsubscribe$))
        .subscribe((configuration) => {
            this.configuration = configuration;
            // Gateway direct access - no APIC logic needed
            this.eventsURL = configuration.HostApi + '/cvv/events';
      });
  }

  updateEventData(data: Events): void {
    this.eventData$.next(data);
  }

  getEventData(): Observable<Events> {
    return this.eventData$.asObservable();
  }

  updateLastUpdateTimestamp(data: any): void {
    this.lastUpdateTimestamp$.next(data);
  }

  getLastUpdateTimestamp(): Observable<any> {
    return this.lastUpdateTimestamp$.asObservable();
  }

  // Convert timestamp to ISO format for backend API calls
  // Backend expects ISO 8601 format compatible with Joda DateTime parsing
  // Must strip fractional seconds as the backend @DateTimeFormat parser can't handle them reliably
  private formatTimestampForApi(timestamp: any): string {
    if (!timestamp) return null;

    let date: Date;

    if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    } else if (Array.isArray(timestamp)) {
      // Backend Joda DateTime serializes as array [year, month, day, hour, min, sec, ms]
      // month is 1-based from Joda but Date constructor needs 0-based
      const [year, month, day, hour = 0, minute = 0, second = 0] = timestamp;
      date = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
    } else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else {
      return null;
    }

    // Format as ISO without fractional seconds: 2026-02-11T18:36:38Z
    return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
  }

  // toggle API threading
  public startEventDeltaInterval(): void {
    this.interval = interval(EVENT_DELTAWORKER_TIMER).subscribe(() => {
      if (useImMemoryWebApi) {
        this.runMainThread();
      } else {
        this.runWorkerThread();
      }
    });
  }

  public stopWorkerInterval(): void {
    this.interval.unsubscribe();
  }

  public runWorkerThread(): any {
    this.logger.debug("Events Delta called on worker thread", 'worker');
    // Skip this poll cycle if profile hasn't loaded yet — prevents sending driver=null
    // and the malformed `?&driver=...` URL that resulted from the prior implementation.
    if (!this.profile?.adId) {
      this.logger.debug("Events Delta skipped: profile/adId not yet available", 'worker');
      return;
    }
    worker.onmessage = ({ data }) => {
      if (data.error) {
        this.logger.error(`Events Delta worker error: ${data.message} (status: ${data.status})`);
        return;
      }
      const parsedData: Events = JSON.parse(data.data)
      this.updateEventData(parsedData);
      this.updateLastUpdateTimestamp(parsedData.lastUpdateTimestamp)
      this.taskCounts = this.processDataService.processData(parsedData, "delta");
      this.logger.debug("Deltas Worker Finished", 'worker')
    };
    worker.onerror = (err) => {
      this.logger.error('Events Delta worker uncaught error:', err);
    };
    this.msalConfigService.getAzureToken().subscribe((token: string) => {
      const lastUpdate = this.lastUpdateTimestamp$.getValue();
      const formattedTimestamp = this.formatTimestampForApi(lastUpdate);
      const params = new URLSearchParams();
      if (formattedTimestamp) {
        params.set('lastUpdateTimestamp', formattedTimestamp);
      }
      params.set('driver', this.profile.adId);
      params.set('currentUnassignedTaskCount', String(this.taskCounts.currentUnassignedTaskCount));
      params.set('currentDriverTaskCount', String(this.taskCounts.currentDriverTaskCount));
      worker.postMessage({
        "url": this.eventsURL + '?' + params.toString(),
        "timeout": null,
        "airplaneMarkersOnMap": JSON.stringify(this.getOutboundMarkersOnMap()),
        "authHeader": `Bearer ${token}`
      });
    })
  }

  public runMainThread(): void {
    this.logger.debug("Events Delta called on main thread", 'worker');
    const noCache = new Date().getTime();
    const headers= new HttpHeaders()
      .set("Content-type", "application/json; charset=utf-8")
      .set("airplaneMarkersOnMap", JSON.stringify(this.getOutboundMarkersOnMap()));
    this.http.get<Events>('assets/data/mock-data/events-delta-response1.json' + '?noCache=' + noCache, {'headers': headers})
      .subscribe((data) => {
        this.updateEventData(data);
        this.logger.debug( `GET Events Delta response:`, data, 'http' );
      });
  }

  public getDeltas(): void{
    this.logger.debug("Delta manual called", 'worker');
    if (!this.profile?.adId) {
      this.logger.debug("Delta manual skipped: profile/adId not yet available", 'worker');
      return;
    }
    const noCache = new Date().getTime();
    const lastUpdate = this.lastUpdateTimestamp$.getValue();
    const formattedTimestamp = this.formatTimestampForApi(lastUpdate);
    const params = new URLSearchParams();
    if (formattedTimestamp) {
      params.set('lastUpdateTimestamp', formattedTimestamp);
    }
    params.set('driver', this.profile.adId);
    params.set('currentUnassignedTaskCount', String(this.taskCounts.currentUnassignedTaskCount));
    params.set('currentDriverTaskCount', String(this.taskCounts.currentDriverTaskCount));
    params.set('noCache', String(noCache));
    const url = this.eventsURL + '?' + params.toString();
    const headers= new HttpHeaders()
      .set("Content-type", "application/json; charset=utf-8")
      .set("outboundTaskMarkersOnMap", JSON.stringify(this.getOutboundMarkersOnMap()));
    this.http.get<any>(url, {'headers': headers, observe: "response"})
      .subscribe((data) => {
        const bodyObj:Events = data.body;
        bodyObj.driverTaskStatus = data.headers.get("driverTaskStatus");
        bodyObj.unassignedTaskStatus = data.headers.get("unassignedTaskStatus");
        this.updateEventData(bodyObj);
        this.taskCounts = this.processDataService.processData(bodyObj, "manual");
        this.logger.debug( `Delta manual response:`, data.body, 'http' );
      });
  }

  getOutboundMarkersOnMap(): Array<Object>{
    return this.outboundAircraftMarkersService.getOutboundMarkersOnMap();
  }

  subscribeToProfile(): void {
    this.profileService.getProfileObs().pipe(takeUntil(this.unsubscribe$))
    .subscribe((profile) => {
        if (profile) {
          this.profile = profile;
        }
      });
  }

   ngOnDestroy(): void {
    this.unsubscribe$.next(true);
    this.unsubscribe$.complete();
  }

}

